import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown } from "@voicecare.ai/ui-library";
import { callAutomationService } from '../../services/callAutomationService';

const DepartmentSelection = ({ 
  value = '', 
  onChange, 
  placeholder = "Select Department",
  facilityId, // ID of the selected facility to filter departments  
  facilityNPI, // NPI of the selected facility (alternative to facilityId)
  themeMode = 'dark',
  currentTheme = {},
  style = {},
  disabled = false
}) => {
  const [departments, setDepartments] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load departments and facilities data
  const loadData = useCallback(async () => {
    if (departments.length === 0 || facilities.length === 0) {
      setIsLoading(true);
      try {
        const [deptData, facilitiesData] = await Promise.all([
          callAutomationService.getDepartments(),
          callAutomationService.getFacilities()
        ]);
        setDepartments(deptData);
        setFacilities(facilitiesData);
        console.log('📚 Loaded departments data:', deptData.length, 'departments');
        console.log('🏢 Loaded facilities data:', facilitiesData.length, 'facilities');
      } catch (error) {
        console.error('❌ Error loading departments/facilities:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [departments.length, facilities.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter departments by facility
  const filterDepartmentsByFacility = useCallback(() => {
    if (!facilityId && !facilityNPI) {
      setFilteredDepartments([]);
      return;
    }

    let filtered = departments;

    // Prioritize facilityId for exact matching
    if (facilityId) {
      // Filter departments that belong to this exact facility
      filtered = departments.filter(dept => dept.facilityId === facilityId);
      console.log(`🔍 Filtered ${filtered.length} departments for facility ID: ${facilityId} (exact match)`);
    } else if (facilityNPI && facilities.length > 0) {
      // Fallback to NPI-based filtering if facilityId not available
      const facilitiesWithNPI = facilities.filter(f => f.value === facilityNPI);
      
      if (facilitiesWithNPI.length === 1) {
        // Single facility with this NPI
        const facility = facilitiesWithNPI[0];
        filtered = departments.filter(dept => dept.facilityId === facility.id);
        console.log(`🔍 Filtered ${filtered.length} departments for facility NPI: ${facilityNPI}`);
      } else if (facilitiesWithNPI.length > 1) {
        // Multiple facilities with same NPI - filter departments from ALL matching facilities
        const facilityIds = facilitiesWithNPI.map(f => f.id);
        filtered = departments.filter(dept => facilityIds.includes(dept.facilityId));
        console.log(`⚠️ Multiple facilities with NPI ${facilityNPI}, showing departments from all (${filtered.length} total)`);
        console.log(`🏥 Facilities: ${facilitiesWithNPI.map(f => f.facilityName).join(', ')}`);
      }
    }

    setFilteredDepartments(filtered);
  }, [departments, facilities, facilityId, facilityNPI]);

  useEffect(() => {
    filterDepartmentsByFacility();
  }, [filterDepartmentsByFacility]);

  // Convert departments to dropdown options format
  const departmentOptions = filteredDepartments.map(dept => ({
    label: dept.name,
    value: dept.name
  }));

  // Find the selected option
  const selectedOption = departmentOptions.find(option => option.value === value) || null;

  // Determine if dropdown should be disabled
  const isDisabled = disabled || !facilityId && !facilityNPI || filteredDepartments.length === 0;

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      onChange(selectedOption.value);
    } else {
      onChange('');
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <Dropdown
        options={departmentOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder={isLoading ? "Loading departments..." : placeholder}
        themeMode={themeMode}
        disabled={isDisabled}
        isSearchable={true}
        width="100%"
        menuPlacement="auto"
      />
    </div>
  );
};

export default DepartmentSelection;